import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { ShoppingBagIcon } from '@heroicons/react/24/outline';
import { shopApi, shopImageUrl } from '../../../utils/shopApi';
import { clearShopProductsCache } from '../shop/useShopProducts';
import { PageHeader, Skeleton, EmptyState, ErrorState } from '../../../components/ui';
import '../booking-shared.css';
import './AdminShopProducts.css';

export const PRODUCTS_PATH = '/booking/admin/shop-products';

const pounds = (pence) => `£${(pence / 100).toFixed(2)}`;
export const errorOf = (err, fallback) => err.response?.data?.error || fallback;

function priceRange(variants) {
  if (!variants.length) return 'No sizes';
  const prices = variants.map(v => v.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? pounds(min) : `${pounds(min)} to ${pounds(max)}`;
}

// Admin, Shop products: the kit a club sells. Each product opens on its own page.
export default function AdminShopProducts() {
  const [products, setProducts] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoadError(false);
    shopApi.getAdminProducts()
      .then(res => setProducts(res.data))
      .catch((err) => { console.error('Could not load products', err); setLoadError(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const move = async (index, by) => {
    const ids = products.map(p => p.id);
    const [id] = ids.splice(index, 1);
    ids.splice(index + by, 0, id);
    setError(null);
    try {
      const res = await shopApi.reorderProducts(ids);
      clearShopProductsCache();
      setProducts(res.data);
    } catch (err) {
      setError(errorOf(err, "Couldn't change the order. Try again."));
    }
  };

  const header = (
    <PageHeader
      back={{ to: '/admin-hub', label: 'Admin' }}
      title="Shop products"
      details={<>What families can buy in the kit shop. Orders are on <Link to="/booking/admin/shop-orders">Shop orders</Link>.</>}
      action={{ label: 'Add a product', short: 'Add', to: `${PRODUCTS_PATH}/new` }}
    />
  );

  if (loadError) {
    return (
      <div className="bk-page bk-page--wide">
        {header}
        <ErrorState title="Couldn't load products" onRetry={load} />
      </div>
    );
  }

  return (
    <div className="bk-page bk-page--wide">
      {header}
      {error && <p className="bk-form-error" role="alert">{error}</p>}
      {!products && <Skeleton rows={4} label="Loading products…" />}
      {products && products.length === 0 && (
        <EmptyState
          icon={ShoppingBagIcon}
          title="No products yet"
          text="Add your first one and it appears in the kit shop."
          action={<Link className="bk-btn bk-btn--primary" to={`${PRODUCTS_PATH}/new`}>Add a product</Link>}
        />
      )}
      {products && products.length > 0 && (
        <ul className="sp-list">
          {products.map((p, i) => {
            const soldOut = p.variants.filter(v => v.isActive && v.stock === 0).length;
            const low = p.variants.filter(v => v.isActive && v.stock > 0 && v.stock <= 3).length;
            return (
              <li key={p.id} className={`bk-card sp-item${p.isActive ? '' : ' sp-item--hidden'}`}>
                {p.images[0]
                  ? <img src={shopImageUrl(p.images[0])} alt="" className="sp-thumb" />
                  : <span className="sp-thumb sp-thumb--none">No photo</span>}
                <Link to={`${PRODUCTS_PATH}/${p.id}`} className="sp-item__main">
                  <strong>{p.name}</strong>
                  <span className="bk-small">
                    {p.variants.length} size{p.variants.length === 1 ? '' : 's'} · {priceRange(p.variants)}
                  </span>
                  <span className="sp-tags">
                    {!p.isActive && <span className="bk-pill bk-pill--off">Hidden</span>}
                    {soldOut > 0 && <span className="bk-pill bk-pill--bad">{soldOut} sold out</span>}
                    {low > 0 && <span className="bk-pill bk-pill--wait">{low} running low</span>}
                  </span>
                </Link>
                <span className="sp-move">
                  <button type="button" className="ui-icon-btn" aria-label={`Move ${p.name} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                    <ChevronUpIcon aria-hidden="true" />
                  </button>
                  <button type="button" className="ui-icon-btn" aria-label={`Move ${p.name} down`} disabled={i === products.length - 1} onClick={() => move(i, 1)}>
                    <ChevronDownIcon aria-hidden="true" />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
